import React from 'react';

import CircularProgress from '@glass/web/components/base/CircularProgress';
import Fab, { FabProps } from '@glass/web/components/base/Fab';
import {
  CENTER,
  PRIMARY_COLOR,
  END,
  LARGE_SIZE,
  START,
  EXTENDED_VARIANT,
} from '@glass/web/modules/theme/constants';
import makeStyles from '@glass/web/modules/theme/makeStyles';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    gap: theme.spacing(1),
  },
  invisible: {
    transition: theme.transitions.create('opacity'),
  },
  invisibleActive: {
    opacity: 0,
  },
  centerLoading: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    margin: 'auto',
  },
}));

export type FabLoadingButtonProps = {
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  color?: FabProps['color'];
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loadingPosition?: typeof START | typeof CENTER | typeof END;
  onClick?: React.MouseEventHandler;
  autoFocus?: boolean;
  component?: React.ElementType;
  params?: Record<string, unknown>;
  size?: FabProps['size'];
  variant?: FabProps['variant'];
  type?: string;
  loadingIndicator?: React.ReactNode;
};

// made to mimic LoadingButton but for use with Fab only, all other components should use LoadingButton
// todo: add generic button tracking
const FabLoadingButton = React.forwardRef(
  (
    {
      // these props are intended to duplicate mui/lab loading button
      loading: isLoading = false,
      disabled: isDisabled = false,
      className,
      children,
      color = PRIMARY_COLOR,
      fullWidth,
      startIcon,
      endIcon,
      loadingPosition: loadingPositionProp,
      onClick,
      autoFocus,
      component = 'button',
      params,
      size = LARGE_SIZE,
      variant = EXTENDED_VARIANT,
      type,
      loadingIndicator: loadingIndicatorProp,
    }: FabLoadingButtonProps,
    ref,
  ) => {
    const { cx, classes } = useStyles();

    // default loading position to that of the icon
    const loadingPosition = loadingPositionProp ?? (startIcon ? START : endIcon ? END : CENTER);

    const loadingIndicator = isLoading
      ? loadingIndicatorProp ?? (
          <CircularProgress
            className={cx(loadingPosition === CENTER ? classes.centerLoading : '')}
            color="inherit"
            // @ts-expect-error font size is a valid prop for CircularProgress
            fontSize="inherit"
            size="1rem"
          />
        )
      : null;

    return (
      <Fab
        ref={ref}
        autoFocus={autoFocus}
        className={cx(classes.root, className)}
        color={color}
        component={component}
        disabled={isLoading || isDisabled}
        fullWidth={fullWidth}
        loading={isLoading}
        onClick={onClick}
        params={params}
        size={size}
        type={type}
        variant={variant}
      >
        {isLoading && loadingPosition === START ? null : startIcon}
        {isLoading && (loadingPosition === START || loadingPosition === CENTER)
          ? loadingIndicator
          : null}
        <span
          className={cx(
            classes.invisible,
            isLoading && loadingPosition === CENTER ? classes.invisibleActive : null,
          )}
        >
          {children}
        </span>
        {isLoading && loadingPosition === END ? loadingIndicator : endIcon}
      </Fab>
    );
  },
);

FabLoadingButton.displayName = 'FabLoadingButton';

export default FabLoadingButton;
